import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ListSkeleton } from "../components/Skeleton";

type NcStatus = "Open" | "In_Progress" | "Awaiting_Verification" | "Closed";
type NcClassification = "Minor" | "Major" | "Observation" | "Opportunity";

interface NcSummary {
  uuid: string;
  reference: string;
  title: string;
  classification: NcClassification;
  status: NcStatus;
  raisedAt: string;
  raisedBy: { fullName: string; erased: boolean } | null;
  relatedClause: {
    standardCode: string;
    versionYear: number;
    clauseReference: string;
  } | null;
}

const STATUS_TONE: Record<NcStatus, string> = {
  Open: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  In_Progress: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-200",
  Awaiting_Verification: "bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-200",
  Closed: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-200",
};

const STATUS_LABEL: Record<NcStatus, string> = {
  Open: "Open",
  In_Progress: "In progress",
  Awaiting_Verification: "Awaiting verification",
  Closed: "Closed",
};

const CLASSIFICATION_TONE: Record<NcClassification, string> = {
  Minor: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  Major: "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-200",
  Observation: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  Opportunity: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200",
};

const formatDateUk = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const STATUS_FILTERS: Array<{ label: string; value: "" | NcStatus }> = [
  { label: "All", value: "" },
  { label: "Open", value: "Open" },
  { label: "In progress", value: "In_Progress" },
  { label: "Awaiting verification", value: "Awaiting_Verification" },
  { label: "Closed", value: "Closed" },
];

export function Nonconformities() {
  const { slug } = useParams<{ slug: string }>();
  const [items, setItems] = useState<NcSummary[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [filter, setFilter] = useState<"" | NcStatus>("");

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    const path = filter ? `/nonconformities?status=${filter}` : "/nonconformities";
    api<{ nonconformities: NcSummary[] }>(path)
      .then((res) => {
        if (!cancelled) setItems(res.nonconformities);
      })
      .catch((err) => {
        if (!cancelled) setErrors(formatApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [filter]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            Nonconformities
          </h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Findings that need correction or improvement, with their corrective actions and
            verification of effectiveness. Auditors can raise NCs; only Admins can close them.
          </p>
        </div>
        <Link
          to={`/o/${slug}/nonconformities/new`}
          className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
        >
          Raise NC
        </Link>
      </div>

      <div role="tablist" className="flex flex-wrap gap-1">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            type="button"
            role="tab"
            aria-selected={filter === f.value}
            onClick={() => setFilter(f.value)}
            className={[
              "rounded-md px-3 py-1 text-xs font-medium transition",
              filter === f.value
                ? "bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700",
            ].join(" ")}
          >
            {f.label}
          </button>
        ))}
      </div>

      {errors.length > 0 ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}

      {items === null ? (
        <ListSkeleton />
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
          No nonconformities yet. Click &quot;Raise NC&quot; to record the first one.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                <th className="px-4 py-2">Ref</th>
                <th className="px-4 py-2">Title</th>
                <th className="px-4 py-2">Class</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Raised</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-700 dark:bg-slate-800">
              {items.map((nc) => (
                <tr key={nc.uuid} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                  <td className="px-4 py-2 align-top font-mono text-xs text-slate-700 dark:text-slate-300">
                    {nc.reference}
                  </td>
                  <td className="px-4 py-2 align-top">
                    <Link
                      to={`/o/${slug}/nonconformities/${nc.uuid}`}
                      className="font-medium text-slate-900 hover:text-brand-700 dark:text-slate-100 dark:hover:text-brand-300"
                    >
                      {nc.title}
                    </Link>
                    {nc.relatedClause ? (
                      <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                        {nc.relatedClause.standardCode.replace(/^ISO/, "ISO ")}:
                        {nc.relatedClause.versionYear} cl. {nc.relatedClause.clauseReference}
                      </p>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 align-top">
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${CLASSIFICATION_TONE[nc.classification]}`}
                    >
                      {nc.classification}
                    </span>
                  </td>
                  <td className="px-4 py-2 align-top">
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${STATUS_TONE[nc.status]}`}
                    >
                      {STATUS_LABEL[nc.status]}
                    </span>
                  </td>
                  <td className="px-4 py-2 align-top text-xs text-slate-600 dark:text-slate-300">
                    {formatDateUk(nc.raisedAt)}
                    {nc.raisedBy ? (
                      <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                        by {nc.raisedBy.erased ? "Anonymous (erased)" : nc.raisedBy.fullName}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
